/*
  Scroll-synced sticky columns for the product page (.product__column-sticky).

  Plain `position: sticky; top: 3rem` pins a column by its top edge. When the
  column is taller than the window (a koi photo plus the video under it), the
  lower part stays off-screen until the whole other column has scrolled past,
  so the video is unreachable on group-koi pages with long option lists.

  Here a tall column scrolls with the page until its bottom edge is on screen,
  then pins there. Scrolling back up it moves down with the page until its top
  edge returns, then pins at the top again. This works by shifting the sticky
  `top` by the scroll delta between the two limits: a pinned column then moves
  exactly like page content, and the browser still handles the section edges.
  Columns that fit the window keep the plain CSS behaviour.
*/
(() => {
  const SELECTOR = '.product__column-sticky';
  const desktop = window.matchMedia('(min-width: 750px)');
  const columns = new Map(); // column -> { cssTop, top }
  let lastY = window.scrollY;
  let frame = 0;
  let measure = true;

  const resizeObserver = new ResizeObserver(() => {
    measure = true;
    schedule();
  });

  // The stylesheet `top` (3rem) is both the top limit and the gap kept below
  // the column when it pins by its bottom edge. Read with the inline override
  // removed so a previous offset does not leak in.
  const cssTop = (column) => {
    column.style.top = '';
    return parseFloat(getComputedStyle(column).top) || 0;
  };

  function run() {
    frame = 0;
    const y = window.scrollY;
    const delta = y - lastY;
    lastY = y;

    const current = document.querySelectorAll(SELECTOR);
    // Columns swapped out by product-info re-renders.
    columns.forEach((_, column) => {
      if (!column.isConnected) {
        resizeObserver.unobserve(column);
        columns.delete(column);
      }
    });

    current.forEach((column) => {
      let state = columns.get(column);
      if (!state) {
        resizeObserver.observe(column);
        state = { cssTop: null, top: null };
        columns.set(column, state);
        measure = true;
      }

      if (measure) {
        // Mobile and quick-add modals render the column static/relative.
        if (!desktop.matches || getComputedStyle(column).position !== 'sticky') {
          column.style.top = '';
          state.cssTop = null;
          state.top = null;
          return;
        }
        state.cssTop = cssTop(column);
        state.minTop = Math.min(state.cssTop, window.innerHeight - column.offsetHeight - state.cssTop);
      }

      if (state.cssTop === null) return;
      if (state.minTop === state.cssTop) {
        column.style.top = '';
        state.top = null;
        return;
      }

      // First time the column is tall (load, restored scroll, grown content):
      // start from where it actually sits so nothing jumps.
      const from = state.top === null ? column.getBoundingClientRect().top : state.top - delta;
      state.top = Math.min(state.cssTop, Math.max(state.minTop, from));
      column.style.top = `${state.top}px`;
    });

    measure = false;
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(run);
  }

  const remeasure = () => {
    measure = true;
    schedule();
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', remeasure);
  desktop.addEventListener('change', remeasure);
  schedule();
})();
